'use client';

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

interface Toast {
  id: number;
  message: string;
  title?: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

interface ToastContextType {
  show: (options: { message: string; title?: string; variant?: Toast['variant']; duration?: number }) => number;
  success: (message: string, title?: string) => number;
  error: (message: string, title?: string) => number;
  warning: (message: string, title?: string) => number;
  info: (message: string, title?: string) => number;
}

const ToastContext = createContext<ToastContextType | null>(null);

const variants = {
  success: {
    icon: CheckCircle2,
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    iconColor: 'text-green-600',
    titleColor: 'text-green-900',
    textColor: 'text-green-700',
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-600',
    titleColor: 'text-red-900',
    textColor: 'text-red-700',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    iconColor: 'text-orange-600',
    titleColor: 'text-orange-900',
    textColor: 'text-orange-700',
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-900',
    textColor: 'text-blue-700',
  },
};

interface ToastItemProps extends Toast {
  onClose: (id: number) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ id, message, title, variant = 'info', onClose }) => {
  const config = variants[variant] || variants.info;
  const Icon = config.icon;

  return (
    <div
      className={`
        flex items-start gap-3 p-4 sm:p-5 rounded-2xl border-2 shadow-xl
        ${config.bgColor} ${config.borderColor}
        animate-in slide-in-from-right duration-300
        w-full sm:min-w-[320px] sm:max-w-[420px]
      `}
    >
      <div className={`flex-shrink-0 ${config.iconColor}`}>
        <Icon size={24} />
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        {title && (
          <p className={`font-black text-sm mb-1 break-words ${config.titleColor}`}>{title}</p>
        )}
        <p className={`font-bold text-sm break-words ${config.textColor}`}>{message}</p>
      </div>
      <button
        onClick={() => onClose(id)}
        className={`flex-shrink-0 p-1 rounded-lg hover:bg-white/50 transition-colors ${config.iconColor}`}
      >
        <X size={18} />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: number) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container fixed top-4 left-4 right-4 sm:left-auto sm:right-6 sm:top-6 z-[200] flex flex-col gap-3">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          {...toast}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(({ message, title, variant = 'info', duration = 4000 }: { message: string; title?: string; variant?: Toast['variant']; duration?: number }) => {
    const id = Date.now() + Math.random();

    setToasts((prev) => [...prev, { id, message, title, variant }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const toast: ToastContextType = {
    show: addToast,
    success: (message, title) => addToast({ message, title, variant: 'success' }),
    error: (message, title) => addToast({ message, title, variant: 'error', duration: 6000 }),
    warning: (message, title) => addToast({ message, title, variant: 'warning' }),
    info: (message, title) => addToast({ message, title, variant: 'info' }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
